import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API, legacyToken } from '../utils/api';

const SubscriptionCtx = createContext({
  plan: 'free', beta: false, loading: true,
  canExport: false, canVersionHistory: false, canRecycleBin: false, canTeamWorkspace: false,
  recordLimit: 10, refetch: () => {},
  extractions: { used: 0, limit: 5, remaining: 5 },
});

function planFeatures(plan, serverFeatures) {
  const paid = ['pro', 'org', 'beta'].includes(plan);
  const org  = ['org', 'beta'].includes(plan);
  return {
    canExport:          serverFeatures ? !!serverFeatures.pptx_export       : paid,
    canVersionHistory:  serverFeatures ? !!serverFeatures.version_history   : paid,
    canRecycleBin:      serverFeatures ? !!serverFeatures.recycle_bin        : paid,
    canTeamWorkspace:   serverFeatures ? !!serverFeatures.team_workspace     : org,
    canPropose:         serverFeatures ? !!serverFeatures.proposal_writing   : paid,
    canEligibility:     serverFeatures ? !!serverFeatures.eligibility_checker: paid,
    canDriveSync:       serverFeatures ? !!serverFeatures.drive_sync         : paid,
    canExcelExport:     serverFeatures ? !!serverFeatures.excel_export       : paid,
    canSchemas:         serverFeatures ? !!serverFeatures.donor_schemas      : paid,
    recordLimit:        plan === 'free' ? 10 : null,
  };
}

// Session cache: returning users see their real plan instantly instead of a
// flash of free-plan UI while /plan-features loads; a background refresh
// then reconciles. sessionStorage clears when the browser closes, so a plan
// change is never stale for more than one tab session.
const PLAN_CACHE_KEY = 'nofodesk_plan_cache_v1';
function loadPlanCache() {
  try { return JSON.parse(sessionStorage.getItem(PLAN_CACHE_KEY) || 'null'); } catch { return null; }
}
function savePlanCache(data) {
  try { sessionStorage.setItem(PLAN_CACHE_KEY, JSON.stringify(data)); } catch {}
}

export function SubscriptionProvider({ children, authUser }) {
  const cached = loadPlanCache();
  const [plan,        setPlan]        = useState(cached?.plan || 'free');
  const [beta,        setBeta]        = useState(!!cached?.beta);
  const [expiresAt,   setExpiresAt]   = useState(cached?.expires_at || null);
  const [loading,     setLoading]     = useState(!cached);
  const [serverFeats, setServerFeats] = useState(cached?.features || null);
  const [extractions, setExtractions] = useState(cached?.extractions || { used: 0, limit: 5, remaining: 5 });

  const fetchPlan = useCallback(async () => {
    const token = legacyToken();   // pre-cookie sessions only
    if (!authUser) { setPlan('free'); setBeta(false); setLoading(false); return; }
    try {
      const res  = await fetch(`${API}/user/plan-features`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
        cache: 'no-store',
      });
      const data = res.ok ? await res.json() : { plan: 'free', beta: false };
      setPlan(data.plan || 'free');
      setBeta(!!data.beta);
      setExpiresAt(data.expires_at || null);
      setServerFeats(data.features || null);
      setExtractions(data.extractions || { used: 0, limit: 5, remaining: 5 });
      if (res.ok) savePlanCache(data);
    } catch {
      setPlan('free');
      setBeta(false);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const value = {
    plan, beta, expiresAt, loading, extractions,
    ...planFeatures(plan, serverFeats),
    refetch: fetchPlan,
  };

  return <SubscriptionCtx.Provider value={value}>{children}</SubscriptionCtx.Provider>;
}

export const useSubscription = () => useContext(SubscriptionCtx);
